import { apiClient } from '@/api/client';
import type {
  AcceptRequestInput,
  BulkResult,
  DeclineRequestInput,
  DecisionResult,
  RequestFilters,
  RequestList,
  RequestSummary,
  SlotConflict,
} from '@/types/appointment-request.types';

/** Drops empty values so the query string carries only real filters. */
const params = (o: object) =>
  Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined && v !== ''),
  );

export const appointmentRequestsApi = {
  async summary(branchId?: string): Promise<RequestSummary> {
    const { data } = await apiClient.get('/appointment-requests/summary', {
      params: params({ branchId }),
    });
    return data;
  },

  async list(filters: RequestFilters): Promise<RequestList> {
    const { data } = await apiClient.get('/appointment-requests', {
      params: params(filters),
    });
    return data;
  },

  /** What accepting with these choices would run into. Never writes. */
  async check(
    id: string,
    input: { doctorId?: string; roomId?: string; scheduledAt?: string },
  ): Promise<{ conflicts: SlotConflict[]; canAccept: boolean }> {
    const { data } = await apiClient.get(`/appointment-requests/${id}/check`, {
      params: params(input),
    });
    return data;
  },

  async accept(id: string, input: AcceptRequestInput): Promise<DecisionResult> {
    const { data } = await apiClient.post(
      `/appointment-requests/${id}/accept`,
      input,
    );
    return data;
  },

  async decline(
    id: string,
    input: DeclineRequestInput,
  ): Promise<DecisionResult> {
    const { data } = await apiClient.post(
      `/appointment-requests/${id}/decline`,
      input,
    );
    return data;
  },

  async bulkAccept(input: {
    ids: string[];
    doctorId?: string;
    notifyPatient?: boolean;
  }): Promise<BulkResult> {
    const { data } = await apiClient.post(
      '/appointment-requests/bulk/accept',
      input,
    );
    return data;
  },

  async bulkDecline(
    input: DeclineRequestInput & { ids: string[] },
  ): Promise<BulkResult> {
    const { data } = await apiClient.post(
      '/appointment-requests/bulk/decline',
      input,
    );
    return data;
  },
};
